/**
 * 생성기 첨부의 사전 등록 — 첨부하자마자 서버 세션에 올려 두어, 제출할 때 업로드·추출을
 * 기다리지 않게 한다. 실패는 null 이다(조용히): 원본을 들고 있으니 제출할 때 실행이 다시 등록한다.
 * React 를 모른다. 언제 시작하고 끊을지는 useAiGenerateDockPreregistration 이 정한다.
 */
import {
  type AiGenerateDockLinkSourceType,
  type AiGenerateDockSourceType,
} from '../consts/aiGenerateDock.const';
import { type AiBuilderSessionPort } from '../stores/aiBuilderSessionStore';
import { type AiGenerateDockAttachment } from '../types/aiGenerateDock.types';

import {
  type AiGenerateDockRegisteredSource,
  type AiGenerateDockSource,
} from './aiGenerateDockRunInput';
import {
  type RegisterSource,
  registerSource,
} from './serverAiBuilderChatTransport';

/** 사전 등록 결과 — 실행이 쓰는 ref·세션에 칩 표시용 제목·길이를 더한다. */
export type AiGenerateDockPreregisteredSource =
  AiGenerateDockRegisteredSource & {
    title: string;
    durationSeconds?: number;
  };

/** 칩이 보는 사전 등록 상태 — 실패·미시작은 없음(undefined)이다. */
export type AiGenerateDockRegistrationView =
  | { status: 'registering' }
  | { status: 'registered'; title: string; durationSeconds?: number };

/** 서버가 영상 제목 대신 돌려주는 video_id 모양(11자). */
const YOUTUBE_VIDEO_ID_PATTERN = /^[\w-]{11}$/;

/** 탭에 붙어 있는 첨부 하나 — 사전 등록의 단위다. `source` 객체 정체성이 곧 등록의 키다. */
export type AiGenerateDockAttachedSource =
  | {
      sourceType: 'file';
      source: Extract<AiGenerateDockSource, { kind: 'file' }>;
    }
  | {
      sourceType: AiGenerateDockLinkSourceType;
      source: Extract<AiGenerateDockSource, { kind: 'link' }>;
    };

/**
 * 탭별 자료에서 사전 등록할 첨부 목록을 뽑는다 — 주제 탭·빈 자료는 빼고, 탭과 자료 종류가
 * 맞는 것만 남긴다. 각 `source` 는 받은 객체 그대로다(등록의 키).
 */
export const listAiGenerateDockAttachedSources = (
  sources: Partial<Record<AiGenerateDockSourceType, AiGenerateDockSource>>,
): AiGenerateDockAttachedSource[] => {
  const attached: AiGenerateDockAttachedSource[] = [];
  if (sources.file?.kind === 'file') {
    attached.push({ sourceType: 'file', source: sources.file });
  }
  const linkTabs: AiGenerateDockLinkSourceType[] = ['youtube', 'website'];
  linkTabs.forEach(sourceType => {
    const source = sources[sourceType];
    if (source?.kind === 'link') attached.push({ sourceType, source });
  });
  return attached;
};

export const preregisterAiGenerateDockSource = async (
  attached: AiGenerateDockAttachedSource,
  port: Pick<AiBuilderSessionPort, 'draftInstanceId' | 'ensureSession'>,
  signal: AbortSignal,
  register: RegisterSource = registerSource,
): Promise<AiGenerateDockPreregisteredSource | null> => {
  try {
    const sessionId = await port.ensureSession();
    if (!sessionId || signal.aborted) return null;

    const base = {
      sessionId,
      draftInstanceId: port.draftInstanceId,
      purpose: 'generator' as const,
      signal,
    };
    const response =
      attached.sourceType === 'file'
        ? await register({
            ...base,
            sourceType: 'file',
            file: attached.source.file,
            fileName: attached.source.fileName,
          })
        : await register({
            ...base,
            sourceType: attached.sourceType,
            url: attached.source.url,
          });

    if (response.status !== 'ready') return null;
    return {
      sourceRef: response.source_ref,
      sessionId,
      title: response.title,
      ...(response.duration_seconds
        ? { durationSeconds: response.duration_seconds }
        : {}),
    };
  } catch {
    // 거절(쿼타·409 등)·추출 실패·중단 모두 조용히 — 제출할 때 다시 등록하며 그때 안내한다.
    return null;
  }
};

/**
 * 사전 등록 상태를 칩에 반영한다.
 * - 등록 중: 파일은 올리는 중, 링크는 가져오는 중(썸네일 진행 표시).
 * - 등록됨: 링크는 서버가 받은 제목(웹 og:title·영상 제목)과 영상 길이를 싣는다. 서버가 영상 제목을
 *   받지 못하면 video_id 를 제목으로 돌려주는데, URL 보다 못하니 그때는 싣지 않는다.
 * - 없음(실패·미시작): 그대로.
 */
export const applyAiGenerateDockRegistration = (
  attachment: AiGenerateDockAttachment,
  view: AiGenerateDockRegistrationView | undefined,
): AiGenerateDockAttachment => {
  if (!view) return attachment;
  if (view.status === 'registering') {
    return attachment.kind === 'file'
      ? { ...attachment, status: 'uploading' }
      : { ...attachment, status: 'fetching' };
  }
  if (attachment.kind === 'file') return attachment;

  const title = view.title.trim();
  const isVideoIdFallback =
    attachment.sourceType === 'youtube' &&
    YOUTUBE_VIDEO_ID_PATTERN.test(title) &&
    attachment.url.includes(title);
  return {
    ...attachment,
    ...(title && !isVideoIdFallback ? { title } : {}),
    ...(view.durationSeconds ? { durationSeconds: view.durationSeconds } : {}),
  };
};
